import { IconNavHome, IconNavPrizes, IconNavProfile, IconNavRefer } from "@assets";
import { PromotionBottomPrompt } from "@components/promotions/PromotionBottomPrompt";
import CoinHeartbeat from "@components/utils/CoinHeartbeat";
import MemberModeProtector from "@components/utils/MemberModeProtector";
import {
  AuthSource,
  useImprovedPromotionWordings,
  usePrizesWordingExperiment,
  useStatsigTracking,
} from "@configs/statsig";
import { PRIZE_PAGES, Paths } from "@constants";
import useCSCompensation from "@hooks/useCSCompensation";
import { useLanguageDirection } from "@hooks/useLanguageDirection";
import { useMegaShareStatus } from "@hooks/useMegaShareStatus";
import usePromotionBottomPromptVisibility from "@hooks/usePromotionBottomPromptVisibility";
import usePromotionStatus from "@hooks/usePromotionStatus";
import useSkinnerBoxStatus from "@hooks/useSkinnerBoxStatus";
import { UserMode } from "@types";
import { MegaShareState } from "@views/PromotionMegaSharePage/types";
import {
  BottomNavigationAction,
  BottomNavigation as BottomNavigationUIKit,
  PropsWithClassName,
  WarningWrapper,
  cn,
} from "kz-ui-sdk";
import { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";
import { CenterButton } from "./components";

interface BottomNavigationProps extends PropsWithChildren, PropsWithClassName {
  userMode: UserMode;
}

const BottomNavigation = ({ className, userMode }: BottomNavigationProps) => {
  const { t } = useTranslation();
  const { trackClickPromotionPage } = useStatsigTracking();
  const direction = useLanguageDirection();
  const location = useLocation();

  const { isAnyClaimable: isPromotionsClaimable, isReferralClaimable, isGiverSecretCodeUnseen } = usePromotionStatus();

  const { shouldShowSkinnerBox, skinnerBoxClaimableAmount } = useSkinnerBoxStatus();
  const { shouldShowCSCompensation } = useCSCompensation();
  const {
    shouldShowTakerMegaShare,
    taker: megaShareTaker,
    details: megaShareDetails,
    isGiverMegaShareUnseen,
  } = useMegaShareStatus();

  const { prizesLabelKey } = usePrizesWordingExperiment();
  const { getPromotionWording } = useImprovedPromotionWordings();

  const megaShareStatus = megaShareTaker?.status;
  const megaShareClaimAmount = megaShareDetails?.takerClaimAmount;

  const shouldShowPromotionAlerts =
    ((shouldShowSkinnerBox && skinnerBoxClaimableAmount > 0) ||
      shouldShowCSCompensation ||
      (shouldShowTakerMegaShare &&
        megaShareStatus === MegaShareState.CLAIMABLE &&
        !!megaShareClaimAmount &&
        megaShareClaimAmount > 0)) ??
    false;

  const shouldShowPrizeAlert = isPromotionsClaimable || isGiverMegaShareUnseen || isGiverSecretCodeUnseen;

  const { shouldShowPrompt: shouldShowPromotionPrompt } = usePromotionBottomPromptVisibility(
    isPromotionsClaimable ?? false,
  );

  return (
    <div dir={direction}>
      <BottomNavigationUIKit className={cn(className)}>
        {/*HOME*/}
        <NavLink
          to={Paths.PRIVATE.HOME}
          className="h-[56px] w-[56px] flex-shrink-0"
        >
          {({ isActive }) => (
            <BottomNavigationAction
              icon={
                <WarningWrapper
                  isShown={shouldShowPromotionAlerts}
                  classes={{
                    "span&": "end-[-4px] top-[-2px] !h-[16px] !w-[16px] !text-[10px] border-2 border-[#291c5a]",
                  }}
                  icon={
                    <CoinHeartbeat
                      className="absolute -end-1 -top-1"
                      imgClassName="h-[13px] w-[13px]"
                      aria-label="Bottom Navigate Home"
                    />
                  }
                >
                  <IconNavHome
                    className={isActive ? "text-content-base" : "text-content-soft"}
                    width={20}
                    height={20}
                    aria-label="Bottom navigation Home"
                  />
                </WarningWrapper>
              }
              label={t("Home")}
              active={isActive}
              className="w-full"
              aria-label="Bottom navigation Home"
            />
          )}
        </NavLink>

        {/*PRIZES*/}
        <div className="relative h-[56px] w-[56px] flex-shrink-0">
          <PromotionBottomPrompt isVisible={shouldShowPromotionPrompt} />
          <NavLink
            to={Paths.PRIVATE.PRIZES}
            className="h-[56px] w-[56px] flex-shrink-0"
            onClick={() => {
              trackClickPromotionPage({
                previousPage: window.location.pathname,
                hasClaimablePromotions: shouldShowPrizeAlert ?? false,
                userMode,
              });
            }}
          >
            {({ isActive }) => {
              // Mark as active when on any prize/promotion page
              const isPrizesActive = isActive || PRIZE_PAGES.has(location.pathname);
              return (
                <BottomNavigationAction
                  icon={
                    <WarningWrapper
                      isShown={shouldShowPrizeAlert ?? false}
                      classes={{
                        "span&": "end-[-4px] top-[-2px] !h-[16px] !w-[16px] !text-[10px] border-2 border-[#291c5a]",
                      }}
                      icon={
                        <CoinHeartbeat
                          className="absolute -end-1 -top-1"
                          imgClassName="h-[13px] w-[13px]"
                          aria-label="Bottom navigation Prizes"
                        />
                      }
                    >
                      <IconNavPrizes
                        width={20}
                        height={20}
                        className={isPrizesActive ? "text-content-base" : "text-content-soft"}
                        aria-label="Bottom navigation Prizes"
                      />
                    </WarningWrapper>
                  }
                  label={t(prizesLabelKey)}
                  className="h-[56px] w-[56px]"
                  active={isPrizesActive}
                  aria-label="Bottom navigation Prizes"
                />
              );
            }}
          </NavLink>
        </div>

        {/*CENTER BUTTON*/}
        <CenterButton userMode={userMode} />

        {/*REFER*/}
        <NavLink
          to={Paths.PRIVATE.PROMOTION_REFER_FRIEND}
          className="h-[56px] w-[56px] flex-shrink-0"
        >
          {({ isActive }) => (
            <>
              <BottomNavigationAction
                icon={
                  <WarningWrapper
                    isShown={isReferralClaimable ?? false}
                    classes={{
                      "span&": "end-[-4px] top-[-2px] !h-[16px] !w-[16px] !text-[10px] border-2 border-[#291c5a]",
                    }}
                    icon={
                      <CoinHeartbeat
                        className="absolute -end-1 -top-1"
                        imgClassName="h-[13px] w-[13px]"
                        aria-label="Bottom navigation Refer"
                      />
                    }
                  >
                    <IconNavRefer
                      width={20}
                      height={20}
                      className={isActive ? "text-content-base" : "text-content-soft"}
                      aria-label="Bottom navigation Refer"
                    />
                  </WarningWrapper>
                }
                label={t(getPromotionWording("Refer"))}
                className="h-[56px] w-[56px]"
                active={isActive}
                classes={{
                  "label&": "whitespace-nowrap",
                }}
                aria-label="Bottom navigation Refer"
              />
            </>
          )}
        </NavLink>

        {/*PROFILE*/}
        <MemberModeProtector registrationSource={AuthSource.BOTTOM_NAVIGATION_PROFILE_BUTTON}>
          <NavLink
            to={Paths.PRIVATE.PROFILE}
            className="h-[56px] w-[56px] flex-shrink-0"
          >
            {({ isActive }) => (
              <BottomNavigationAction
                icon={
                  <IconNavProfile
                    width={22}
                    height={22}
                    className={isActive ? "text-content-base" : "text-content-soft"}
                    aria-label="Bottom navigation Profile"
                  />
                }
                label={t("Profile")}
                active={isActive}
                className="w-full"
                aria-label="Bottom navigation Profile"
              />
            )}
          </NavLink>
        </MemberModeProtector>
      </BottomNavigationUIKit>
    </div>
  );
};

export default BottomNavigation;
